// =============================================================
// File: src/components/Widgets/CostMixerWidget.jsx
// Theme-aware + high-contrast text (white on dark, black on light)
// NumericPad legibility improvements
// Optional: disable auto keypad on chip add (AUTO_EDIT_ON_ADD)
// =============================================================

import React, { useMemo, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

/* ---------- Optional contexts (fail-soft) ---------- */
let useCalculatorHook = null;
let useMaterialHook = null;
let useFeedbackHook = null;
try { useCalculatorHook = require("../../contexts/CalculatorContext").useCalculator; } catch {}
try {
  useMaterialHook =
    require("../../contexts/MaterialContext").useMaterial ||
    require("../../contexts/MaterialContext").useMaterials;
} catch {}
try { useFeedbackHook = require("../../contexts/FeedbackContext").useFeedback; } catch {}
let SoundControlsInline = () => null;
try { SoundControlsInline = require("../Controls/SoundControlsInline").default || (() => null); } catch {}

/* ---------- Helpers ---------- */
function money(n, currency = "AUD") {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(n || 0));
  } catch {
    return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}
function cls(...xs) { return xs.filter(Boolean).join(" "); }

const DEFAULT_CHIPS = [
  { key: "clay",  label: "Clay",  unit: "kg",    icon: "Brick", unitCost: 0 },
  { key: "glaze", label: "Glaze", unit: "batch", icon: "Palette", unitCost: 0 },
  { key: "kiln",  label: "Kiln",  unit: "hr",    icon: "Kiln", unitCost: 0 },
  { key: "staff", label: "Staff", unit: "hr",    icon: "Staff", unitCost: 0 },
  { key: "other", label: "Other", unit: "ea",    icon: "+", unitCost: 0 },
];

/* ---------- Color utils ---------- */
function getCssVar(name, fallback = "") {
  try { return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback; }
  catch { return fallback; }
}
function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const b = h.length === 3 ? [...h].map(x => x + x).join("") : h;
  const r = parseInt(b.slice(0,2),16), g = parseInt(b.slice(2,4),16), bl = parseInt(b.slice(4,6),16);
  return { r, g, b: bl };
}
function rgba(color, a) {
  if (!color) return `rgba(0,0,0,${a})`;
  const c = color.toLowerCase();
  if (c.startsWith("#")) { const { r,g,b } = hexToRgb(c); return `rgba(${r},${g},${b},${a})`; }
  if (c.startsWith("rgb(")) return c.replace("rgb(", "rgba(").replace(")", `, ${a})`);
  return color;
}

/* ---------- NumericPad (mini keypad modal) ---------- */
function NumericPad({ isOpen, onClose, value, onChange, label = "Amount", isDark }) {
  const dlgRef = useRef(null);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  if (!isOpen) return null;

  const textColor = isDark ? "white" : "#0f172a"; // slate-900
  const fieldBg   = isDark ? "rgba(15,23,42,0.7)" : "rgba(255,255,255,0.85)"; // matches dark/light

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div
        ref={dlgRef}
        className="relative w-full max-w-sm rounded-t-2xl border p-4 backdrop-blur-xl"
        style={{
          background: isDark ? "rgba(2,6,23,0.85)" : "rgba(255,255,255,0.9)",
          borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)",
          color: textColor,
        }}
      >
        <div className="text-sm mb-1" style={{ opacity: 0.85 }}>{label}</div>
        <input
          className="w-full mb-2 px-3 py-2 rounded-lg border outline-none"
          type="number"
          step="0.01"
          value={value}
          onChange={(e) => onChange?.(Number(e.target.value))}
          style={{
            background: fieldBg,
            borderColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)",
            color: textColor,
          }}
        />
        <div className="grid grid-cols-3 gap-2 text-lg select-none">
          {[1,2,3,4,5,6,7,8,9,0].map((n) => (
            <button
              key={n}
              className="rounded-xl py-3 border active:scale-[0.98]"
              onClick={() => onChange?.(Number(`${value || 0}${n}`))}
              style={{
                background: fieldBg,
                borderColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)",
                color: textColor,
              }}
            >
              {n}
            </button>
          ))}
          <button
            className="col-span-3 rounded-xl py-3 active:scale-[0.98]"
            onClick={onClose}
            style={{
              background: isDark ? "#111827" : "#111827", // neutral dark button
              color: "white",
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
NumericPad.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  label: PropTypes.string,
  isDark: PropTypes.bool,
};

/* ---------- Ingredient Chip ---------- */
function IngredientChip({ icon, label, onClick, active, primary, isDark }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1.5 rounded-full border text-sm transition backdrop-blur"
      style={{
        background: isDark ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.6)",
        borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.18)",
        color: isDark ? "white" : "#0f172a",
        boxShadow: active ? `0 0 0 2px ${rgba(primary, 0.9)}` : undefined,
      }}
    >
      <span className="mr-1">{icon}</span>{label}
    </button>
  );
}
IngredientChip.propTypes = {
  icon: PropTypes.node, label: PropTypes.string, onClick: PropTypes.func,
  active: PropTypes.bool, primary: PropTypes.string, isDark: PropTypes.bool,
};

/* ---------- Main Widget ---------- */
export default function CostMixerWidget({ onSave }) {
  // Toggle: open keypad after tapping a chip?
  const AUTO_EDIT_ON_ADD = true; // set to false if you want chips to just add rows without opening the pad

  // Theme detection
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark") ||
    window.matchMedia?.("(prefers-color-scheme: dark)")?.matches
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    const cb = (e) => setIsDark(e.matches || document.documentElement.classList.contains("dark"));
    mq?.addEventListener?.("change", cb);
    const onTheme = () => cb({ matches: document.documentElement.classList.contains("dark") });
    window.addEventListener("mudo:theme-changed", onTheme);
    return () => { mq?.removeEventListener?.("change", cb); window.removeEventListener("mudo:theme-changed", onTheme); };
  }, []);

  // Primary color
  const [primary, setPrimary] = useState("#F97316");
  useEffect(() => {
    const update = () => setPrimary(getCssVar("--color-primary", "#F97316"));
    update();
    window.addEventListener("mudo:theme-changed", update);
    return () => window.removeEventListener("mudo:theme-changed", update);
  }, []);

  const calc = useCalculatorHook ? useCalculatorHook() : { favourites: [] };
  const mat  = useMaterialHook ? useMaterialHook() : { materials: [] };
  const feedback = useFeedbackHook ? useFeedbackHook() : { toast: () => {} };

  const favourites = useMemo(() => (Array.isArray(calc?.favourites) ? calc.favourites : []), [calc]);
  const materials  = useMemo(() => (Array.isArray(mat?.materials) ? mat.materials : []), [mat]);

  const [mode, setMode] = useState("piece");
  const [items, setItems] = useState([]);
  const [padOpen, setPadOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editField, setEditField] = useState("qty");
  const currency = "AUD";

  const chips = useMemo(() => {
    const map = new Map((materials || []).map((m) => [String(m.name || m.title || "").toLowerCase(), m]));
    return DEFAULT_CHIPS.map((c) => {
      const m = map.get(c.label.toLowerCase());
      return { ...c, unitCost: Number(m?.unitCost ?? m?.price ?? c.unitCost ?? 0) };
    });
  }, [materials]);

  const total = useMemo(() => items.reduce((a, b) => a + Number(b.subtotal || 0), 0), [items]);

  const [markupPct, setMarkupPct] = useState(80);
  const suggestedRetail = useMemo(() => total * (1 + (Number(markupPct) || 0) / 100), [total, markupPct]);
  const profitMargin = useMemo(() => (suggestedRetail > 0 ? ((suggestedRetail - total) / suggestedRetail) * 100 : 0), [suggestedRetail, total]);

  // Haptic/soft click when SRP crosses a $10 bucket
  const [prevSrp, setPrevSrp] = useState(null);
  useEffect(() => {
    if (prevSrp != null && suggestedRetail > 0) {
      const prevBucket = Math.floor(prevSrp / 10);
      const currBucket = Math.floor(suggestedRetail / 10);
      if (prevBucket !== currBucket) {
        try { navigator?.vibrate?.(12); } catch {}
        try {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (Ctx) {
            const ctx = new Ctx(), o = ctx.createOscillator(), g = ctx.createGain();
            o.type = "sine"; o.frequency.value = 520; g.gain.value = 0.03; o.connect(g); g.connect(ctx.destination);
            o.start(); setTimeout(() => { o.stop(); ctx.close(); }, 60);
          }
        } catch {}
      }
    }
    setPrevSrp(suggestedRetail);
  }, [suggestedRetail]);

  // Sparkles + SRP lock
  const [showSparkles, setShowSparkles] = useState(false);
  const triggerSparkles = () => { setShowSparkles(true); setTimeout(() => setShowSparkles(false), 700); };
  const [srpLockEnabled, setSrpLockEnabled] = useState(false);
  const [targetSrp, setTargetSrp] = useState(0);
  useEffect(() => {
    if (srpLockEnabled && total > 0 && targetSrp > 0) {
      const pct = Math.max(0, (targetSrp / total - 1) * 100);
      if (Number.isFinite(pct)) setMarkupPct(Math.round(pct));
    }
  }, [total, targetSrp, srpLockEnabled]);

  /* ---------- Item ops ---------- */
  function addItem(chip) {
    const next = { key: chip.key, label: chip.label, unit: chip.unit, qty: 1, unitCost: Number(chip.unitCost || 0), subtotal: Number(chip.unitCost || 0) };
    setItems((xs) => [...xs, next]);
    if (AUTO_EDIT_ON_ADD) {
      setEditingIndex(items.length);
      setPadOpen(true);
    }
  }
  function updateQty(i, qty) {
    setItems((xs) => xs.map((it, idx) => idx === i ? { ...it, qty: Number(qty || 0), subtotal: Number(qty || 0) * Number(it.unitCost || 0) } : it));
  }
  function updateUnitCost(i, unitCost) {
    setItems((xs) => xs.map((it, idx) => idx === i ? { ...it, unitCost: Number(unitCost || 0), subtotal: Number(it.qty || 0) * Number(unitCost || 0) } : it));
  }
  const removeItem = (i) => setItems((xs) => xs.filter((_, idx) => idx !== i));
  const clearAll   = () => setItems([]);

  /* ---------- Presets ---------- */
  function applyPreset(name) {
    const baseMap = { "clay-glaze-kiln": ["clay", "glaze", "kiln"], "clay-glaze-staff": ["clay", "glaze", "staff"] };
    if (baseMap[name]) {
      const toAdd = chips.filter((c) => baseMap[name].includes(c.key));
      setItems(toAdd.map((c) => ({ key: c.key, label: c.label, unit: c.unit, qty: 1, unitCost: Number(c.unitCost || 0), subtotal: Number(c.unitCost || 0) })));
      triggerSparkles();
      return;
    }
    if (name === "batch10") {
      setItems((xs) => xs.map((x) => ({ ...x, qty: Number(x.qty || 1) * 10, subtotal: Number(x.unitCost || 0) * Number((x.qty || 1) * 10) })));
      triggerSparkles();
    }
  }

  /* ---------- Basis favourites ---------- */
  const basisQuick = useMemo(() => (favourites || []).map((f) => ({
    name: f.name || f.title || "Favourite",
    clay: Number(f?.defaults?.clayKg || f?.clayKg || 0),
    glaze: Number(f?.defaults?.glazeBatch || f?.glazeBatch || 0),
    kiln: Number(f?.defaults?.kilnHr || f?.kilnHr || 0),
    staff: Number(f?.defaults?.staffHr || f?.staffHr || 0),
    other: Number(f?.defaults?.otherCost || f?.otherCost || 0),
    markup: Number(f?.defaults?.markup || f?.markup || 0),
  })), [favourites]);

  function applyBasis(b) {
    const map = new Map(chips.map((c) => [c.key, c])); const next = [];
    ["clay","glaze","kiln","staff"].forEach((k) => {
      const q = b[k]; if (!q) return; const c = map.get(k);
      next.push({ key: k, label: c.label, unit: c.unit, qty: q, unitCost: c.unitCost, subtotal: q * c.unitCost });
    });
    if (b.other) next.push({ key: "other", label: "Other", unit: "ea", qty: 1, unitCost: b.other, subtotal: b.other });
    setItems(next); if (Number.isFinite(b.markup) && b.markup >= 0) setMarkupPct(Number(b.markup));
  }

  /* ---------- Save ---------- */
  function handleSave() {
    const payload = { id: `mix_${Date.now()}`, mode, currency, items, total, createdAt: new Date().toISOString() };
    try { localStorage.setItem("mudo:lastCostMix", JSON.stringify(payload)); } catch {}
    try { window.dispatchEvent(new CustomEvent("mudo:costplanner-save", { detail: payload })); } catch {}
  onSave?.(payload); feedback.toast?.("Added to Cost Planner");
  }

  /* ---------- Theming styles ---------- */
  const baseText   = isDark ? "white" : "#0f172a";
  const borderCol  = isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.18)";
  const glassStyle = {
    color: baseText,
    background: `linear-gradient(180deg, ${rgba(primary, isDark ? 0.22 : 0.12)} 0%, ${rgba(primary, isDark ? 0.10 : 0.06)} 60%, ${rgba(primary, isDark ? 0.07 : 0.04)} 100%)`,
    borderColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.12)",
  };
  const cardStyle  = {
    borderColor: borderCol,
    boxShadow: `inset 0 1px 0 ${rgba(isDark ? "#fff" : "#000", isDark ? 0.15 : 0.08)}`,
    color: baseText,
  };
  const pillStyle  = {
    borderColor: borderCol,
    background: isDark ? "rgba(15,23,42,0.4)" : "rgba(255,255,255,0.5)",
    color: baseText,
  };
  const activePill = { background: rgba(primary, 0.9), color: "white", borderColor: rgba(primary, 0.9) };
  const subtleBtn  = { borderColor: borderCol, background: isDark ? "rgba(15,23,42,0.5)" : "rgba(255,255,255,0.6)", color: baseText };
  const primaryBtn = { background: primary, color: "white" };

  return (
    <div className="rounded-2xl border p-4 backdrop-blur-xl" style={glassStyle}>
      {/* Mode */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium" style={{ opacity: 0.85 }}>Mudo Cost Mixer</div>
        <div className="flex items-center gap-2 text-xs">
          {["piece","batch"].map((m) => (
            <span
              key={m}
              className="px-2 py-0.5 rounded-full border cursor-pointer"
              style={mode === m ? activePill : pillStyle}
              onClick={() => setMode(m)}
              role="button"
            >
              {m === "piece" ? "Per Piece" : "Per Batch"}
            </span>
          ))}
        </div>
      </div>

      {/* Totals */}
      <div className="rounded-xl border p-4 mb-3" style={{ ...cardStyle, background: isDark ? "rgba(2,6,23,0.5)" : "rgba(255,255,255,0.6)" }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs" style={{ opacity: 0.7 }}>Estimated Cost</div>
            <div className="text-2xl font-semibold">{money(total, "AUD")}</div>
            <div className="mt-1 text-xs" style={{ opacity: 0.7 }}>
              Suggested Retail (with markup)
              <span className="ml-2 font-medium text-sm" style={{ opacity: 0.9 }}>{money(suggestedRetail, "AUD")}</span>
              <span className="ml-2 px-1.5 py-0.5 rounded-full border text-[10px]" style={pillStyle}>
                Margin ~ {profitMargin.toFixed(0)}%
              </span>
            </div>
          </div>
          <div className="text-right text-[10px]" style={{ opacity: 0.7 }}>
            <div>Edit Field</div>
            <div className="flex gap-1 mt-1">
              {["qty","unitCost"].map((f) => (
                <button key={f} className="px-2 py-0.5 rounded border" style={editField === f ? activePill : pillStyle} onClick={() => setEditField(f)}>
                  {f === "qty" ? "Qty" : "Unit Cost"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Markup + SRP Lock */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs mb-1" style={{ opacity: 0.7 }}>
            <span>Markup</span>
            <div className="flex items-center gap-2">
              {[50,100,150].map((p) => (
                <button key={p} className="px-1.5 py-0.5 rounded border" style={subtleBtn} onClick={() => setMarkupPct(p)}>
                  ×{(p/50).toFixed(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input type="range" min={0} max={200} step={1} value={markupPct} onChange={(e) => setMarkupPct(Number(e.target.value))} className="flex-1" style={{ accentColor: primary }} />
            <input type="number" className="w-16 px-2 py-1 rounded border text-xs" value={markupPct} onChange={(e) => setMarkupPct(Number(e.target.value))}
                   style={{ ...pillStyle, background: isDark ? "rgba(15,23,42,0.55)" : "rgba(255,255,255,0.75)" }} />
            <span className="text-xs" style={{ opacity: 0.7 }}>%</span>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs">
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={srpLockEnabled} onChange={(e)=> setSrpLockEnabled(e.target.checked)} style={{ accentColor: primary }} />
              Lock SRP
            </label>
            <input type="number" placeholder="Target SRP" className="w-28 px-2 py-1 rounded border"
                   value={targetSrp || ""} onChange={(e)=> setTargetSrp(Number(e.target.value))}
                   style={{ ...pillStyle, background: isDark ? "rgba(15,23,42,0.55)" : "rgba(255,255,255,0.75)" }} />
            <button className="px-2 py-1 rounded border" style={subtleBtn}
              onClick={() => { if(total>0 && targetSrp>0){ const pct = Math.max(0, (targetSrp/total - 1)*100); if (Number.isFinite(pct)) setMarkupPct(Math.round(pct)); } }}>
              Back-solve
            </button>
          </div>
        </div>
      </div>

      {/* Chips */}
      <div className="flex flex-wrap gap-2 mb-3">
        {chips.map((c) => (
          <IngredientChip key={c.key} icon={c.icon} label={c.label} onClick={() => addItem(c)} active={false} primary={primary} isDark={isDark} />
        ))}
      </div>

      {/* Items */}
      <div className="space-y-2 mb-2 max-h-52 overflow-auto pr-1">
        {items.length === 0 && (
          <div className="text-xs" style={{ opacity: 0.6 }}>
            Add components using the chips above, or apply a preset below.
          </div>
        )}
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 rounded-lg border p-2"
               style={{ borderColor: borderCol, background: isDark ? "rgba(2,6,23,0.5)" : "rgba(255,255,255,0.55)", color: baseText }}>
            <div className="w-20 text-xs" style={{ opacity: 0.7 }}>{it.label}</div>
            <button
              className="flex-1 px-2 py-1 rounded border text-left"
              onClick={() => { setEditingIndex(i); setPadOpen(true); }}
              style={{ borderColor: borderCol, background: isDark ? "rgba(15,23,42,0.55)" : "rgba(255,255,255,0.75)", color: baseText }}
            >
              <div className="text-[10px]" style={{ opacity: 0.6 }}>{editField === "qty" ? `Qty (${it.unit})` : "Unit Cost"}</div>
              <div className="text-sm font-medium">{editField === "qty" ? `${it.qty}` : money(it.unitCost, currency)}</div>
            </button>
            <div className="w-24 text-right text-sm font-medium">{money(it.subtotal, currency)}</div>
            <button className="ml-1 text-xs px-2 py-1 rounded-full text-white" style={{ background: "rgba(239,68,68,0.9)" }} onClick={() => removeItem(i)}>
              Remove
            </button>
          </div>
        ))}
      </div>

      {/* Breakdown */}
      {items.length > 0 && (
        <div className="mb-3 text-[11px]" style={{ opacity: 0.8 }}>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {items.map((it, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <span className="px-1.5 py-0.5 rounded-full border" style={pillStyle}>{it.label}</span>
                <span>= {money(it.subtotal, currency)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Presets */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar mb-3 relative">
        <button className="px-3 py-1 rounded-full border" style={subtleBtn} onClick={() => applyPreset("clay-glaze-kiln")}>Clay+Glaze+Kiln</button>
        <button className="px-3 py-1 rounded-full border" style={subtleBtn} onClick={() => applyPreset("clay-glaze-staff")}>Clay+Glaze+Staff</button>
        <button className="px-3 py-1 rounded-full border" style={subtleBtn} onClick={() => applyPreset("batch10")}>Batch ×10</button>
  {showSparkles && <></>}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button className="px-3 py-2 rounded-xl" style={primaryBtn} onClick={handleSave}>Save as Product</button>
        <div className="flex items-center gap-2">
          <SoundControlsInline compact />
          <button className="px-3 py-2 rounded-xl border" style={subtleBtn} onClick={clearAll}>Reset</button>
        </div>
      </div>

      {/* NumericPad */}
      <NumericPad
        isOpen={padOpen}
        onClose={() => setPadOpen(false)}
        value={(() => { const it = items[editingIndex]; if (!it) return 0; return editField === "qty" ? it.qty : it.unitCost; })()}
        onChange={(v) => { if (editingIndex < 0) return; if (editField === "qty") updateQty(editingIndex, v); else updateUnitCost(editingIndex, v); }}
        label={(() => { const it = items[editingIndex]; if (!it) return "Amount"; return editField === "qty" ? `Qty (${it.unit})` : `Unit Cost (${currency})`; })()}
        isDark={isDark}
      />
    </div>
  );
}

CostMixerWidget.propTypes = { onSave: PropTypes.func };
